interface SocialSecurityInput {
  currentAge: number
  birthYear: number
  estimatedFRABenefit: number  // monthly benefit at FRA from SSA statement
}

interface ClaimingScenario {
  age: number
  monthlyBenefit: number
  annualBenefit: number
  reductionOrIncrease: string
  cumulativeByAge: { age: number; cumulative: number }[]
}

function getFullRetirementAge(birthYear: number): number {
  if (birthYear <= 1937) return 65
  if (birthYear === 1938) return 65 + 2 / 12
  if (birthYear === 1939) return 65 + 4 / 12
  if (birthYear === 1940) return 65 + 6 / 12
  if (birthYear === 1941) return 65 + 8 / 12
  if (birthYear === 1942) return 65 + 10 / 12
  if (birthYear >= 1943 && birthYear <= 1954) return 66
  if (birthYear === 1955) return 66 + 2 / 12
  if (birthYear === 1956) return 66 + 4 / 12
  if (birthYear === 1957) return 66 + 6 / 12
  if (birthYear === 1958) return 66 + 8 / 12
  if (birthYear === 1959) return 66 + 10 / 12
  return 67 // 1960 and later
}

function calculateBenefitAtAge(fraMonthlyBenefit: number, fra: number, claimingAge: number): number {
  const monthsEarly = Math.max(0, (fra - claimingAge) * 12)
  const monthsLate = Math.max(0, (claimingAge - fra) * 12)

  if (monthsEarly > 0) {
    // Reduction for claiming early
    // First 36 months: 5/9 of 1% per month = 6.67% per year
    // Beyond 36 months: 5/12 of 1% per month = 5% per year
    const first36 = Math.min(monthsEarly, 36)
    const beyond36 = Math.max(0, monthsEarly - 36)
    const reductionPercent = (first36 * 5 / 9 / 100) + (beyond36 * 5 / 12 / 100)
    return fraMonthlyBenefit * (1 - reductionPercent)
  }

  if (monthsLate > 0) {
    // Delayed retirement credits: 8% per year (2/3 of 1% per month)
    const maxDelayMonths = Math.min(monthsLate, (70 - fra) * 12) // can't earn credits past 70
    const increasePercent = maxDelayMonths * 2 / 3 / 100
    return fraMonthlyBenefit * (1 + increasePercent)
  }

  return fraMonthlyBenefit
}

export function calculateSocialSecurity(input: SocialSecurityInput) {
  const { currentAge, birthYear, estimatedFRABenefit } = input

  const fra = getFullRetirementAge(birthYear)
  const fraYears = Math.floor(fra)
  const fraMonths = Math.round((fra - fraYears) * 12)

  // Calculate benefits at key ages
  const benefitAt62 = calculateBenefitAtAge(estimatedFRABenefit, fra, 62)
  const benefitAtFRA = estimatedFRABenefit
  const benefitAt70 = calculateBenefitAtAge(estimatedFRABenefit, fra, 70)

  // Reduction/increase percentages
  const reductionAt62 = ((estimatedFRABenefit - benefitAt62) / estimatedFRABenefit) * 100
  const increaseAt70 = ((benefitAt70 - estimatedFRABenefit) / estimatedFRABenefit) * 100

  // Cumulative benefits analysis (from each claiming age to 95)
  const maxAge = 95

  function buildCumulative(startAge: number, monthlyBenefit: number): { age: number; cumulative: number }[] {
    const data: { age: number; cumulative: number }[] = []
    let cumulative = 0
    for (let age = 62; age <= maxAge; age++) {
      if (age >= startAge) {
        cumulative += monthlyBenefit * 12
      }
      data.push({ age, cumulative: Math.round(cumulative) })
    }
    return data
  }

  const cumulative62 = buildCumulative(62, benefitAt62)
  const cumulativeFRA = buildCumulative(Math.ceil(fra), benefitAtFRA)
  const cumulative70 = buildCumulative(70, benefitAt70)

  // Break-even: when does claiming at 70 overtake claiming at 62?
  let breakEvenVs62 = 0
  for (let age = 70; age <= maxAge; age++) {
    const idx = age - 62
    if (cumulative70[idx]?.cumulative >= cumulative62[idx]?.cumulative) {
      breakEvenVs62 = age
      break
    }
  }

  // Break-even: when does claiming at FRA overtake claiming at 62?
  let breakEvenFRAVs62 = 0
  const fraStartAge = Math.ceil(fra)
  for (let age = fraStartAge; age <= maxAge; age++) {
    const idx = age - 62
    if (cumulativeFRA[idx]?.cumulative >= cumulative62[idx]?.cumulative) {
      breakEvenFRAVs62 = age
      break
    }
  }

  // Spousal benefit (50% of higher earner's FRA benefit)
  const spousalBenefit = Math.round(estimatedFRABenefit * 0.5)

  // Build combined cumulative data for charting
  const cumulativeData = []
  for (let age = 62; age <= maxAge; age++) {
    const idx = age - 62
    cumulativeData.push({
      age,
      at62: cumulative62[idx]?.cumulative || 0,
      atFRA: cumulativeFRA[idx]?.cumulative || 0,
      at70: cumulative70[idx]?.cumulative || 0,
    })
  }

  return {
    fra,
    fraDisplay: fraMonths > 0 ? `${fraYears} and ${fraMonths} months` : `${fraYears}`,

    benefitAt62: Math.round(benefitAt62),
    benefitAtFRA: Math.round(benefitAtFRA),
    benefitAt70: Math.round(benefitAt70),

    annualAt62: Math.round(benefitAt62 * 12),
    annualAtFRA: Math.round(benefitAtFRA * 12),
    annualAt70: Math.round(benefitAt70 * 12),

    reductionAt62: Math.round(reductionAt62 * 10) / 10,
    increaseAt70: Math.round(increaseAt70 * 10) / 10,

    breakEvenVs62,
    breakEvenFRAVs62,
    spousalBenefit,

    cumulativeData,
  }
}
